/**
 * Derives the "Needs Your Attention" model for a TAS application's course-management
 * (`manage`) screen — a pure function over the application's data so it can be unit
 * tested without rendering. Mirrors the 9 distinct states in the Figma "Application
 * Review" concept (collapsed/expanded duplicates collapse to these 9).
 *
 * Task vs anchor:
 * - TASKS are actionable (Use CourseCash, Upload receipt, Submit evidence): they carry
 *   a due/use-by chip + CTA and count toward the "(N tasks pending)" header + right-rail
 *   Tasks list. A task can be `completed` (shown in the rail, NOT as an NYA card).
 * - ANCHORS are informational (submitted, evidence-submitted, approved,
 *   course-cash-used, all-caught-up): no chip/CTA, never counted.
 *
 * The panel has three zero-task shapes: the submitted anchor, the all-caught-up empty
 * state, and "hidden entirely" (`visible: false`).
 *
 * TODO(PR#2 wiring): confirm the live sources for `courseCashUseByDate` and
 * `evidenceDueDate` — they are placeholders ([mm/dd]) in the design and have no
 * confirmed model field yet. `receiptDueDate`/`receiptUploaded`/`receiptStatus`
 * are wired (ManagePage derives them from the live CourseCash transaction —
 * see `fetchCourseCashTransactions`). The derivation is shaped to accept the
 * rest so wiring is a data-plumbing change, not a logic change.
 */

export type AttentionTaskKind = 'use-course-cash' | 'upload-receipt' | 'submit-evidence';

export type AttentionAnchorKind =
  | 'submitted'
  | 'evidence-submitted'
  | 'approved'
  | 'course-cash-used'
  | 'all-caught-up'
  | 'abandoned';

export type AttentionTaskStatus = 'pending' | 'in-progress' | 'completed';

/**
 * Deadline-driven urgency for a task card (Figma: Default/Warning/Error rows
 * of the "Task: Provide Your CourseCa$h™ Receipt" spec — green/on-swipe,
 * yellow/nearing-due-date, red/blocked). Only `upload-receipt` sets this today.
 */
export type AttentionTaskUrgency = 'default' | 'warning' | 'error';

export interface AttentionTask {
  id: string;
  kind: AttentionTaskKind;
  status: AttentionTaskStatus;
  /** ISO date string for the "due mm/dd" / "use by mm/dd" chip, when known. */
  dueDate?: string | null;
  /** Icon/chip color treatment; defaults to 'default' when not set. */
  urgency?: AttentionTaskUrgency;
  /** Swipe amount in cents, when this task is tied to a specific live transaction (upload-receipt only). */
  amount?: number | null;
  /** ISO date of the swipe, when known (upload-receipt only) — distinguishes concurrent receipt tasks. */
  transactionDate?: string | null;
}

export interface AttentionAnchor {
  kind: AttentionAnchorKind;
  /** Approval total in cents, for the `approved` anchor's "Your approval total is $X". */
  approvedTotal?: number;
}

export interface AttentionModel {
  /** false → render no Needs Your Attention panel at all (the suppressed variant). */
  visible: boolean;
  anchors: AttentionAnchor[];
  /** All tasks incl. completed (the right-rail Tasks list); the NYA panel renders only the non-completed ones as cards. */
  tasks: AttentionTask[];
  /** Count of non-completed tasks → the "(N tasks pending)" header. */
  pendingCount: number;
}

/**
 * One CourseCash swipe still needing a receipt (Figma: "one task per swipe" —
 * each is its own card, not collapsed into a single application-wide task).
 */
export interface ReceiptTaskInput {
  /** The live transaction's id — becomes part of the task id so each swipe gets its own card. */
  id: string;
  dueDate?: string | null;
  /** The live transaction's receiptStatus, when known — drives this task's urgency. */
  status?: string | null;
  /** Swipe amount in cents — surfaced in the card title so concurrent tasks are distinguishable. */
  amount?: number | null;
  /** ISO date of the swipe — surfaced in the card body for the same reason. */
  transactionDate?: string | null;
}

/**
 * The slice of `TasApplicationExtension` the derivation reads. The real model
 * satisfies this structurally; tests pass plain stubs.
 */
export interface AttentionInput {
  state: string;
  amounts?: { course_cash?: { reserved?: number; used?: number } | null } | null;
  isEvidenceRequired: boolean;
  isMissingRequiredEvidence: boolean;
  /**
   * Independent of isEvidenceRequired/isMissingRequiredEvidence (completion
   * documents) — some templates require a course grade but no documents, so
   * the submit-evidence task must key off either requirement, not just the
   * document one.
   */
  isCourseGradeRequired?: boolean;
  isMissingCourseGrades?: boolean;
  isApproved: boolean;
  approvedTotal: number;
  /** Optional due/use-by dates (see TODO above). */
  courseCashUseByDate?: string | null;
  evidenceDueDate?: string | null;
  /**
   * One upload-receipt task per live CourseCash transaction still needing a
   * receipt. When provided (even as an empty array), this is authoritative —
   * ManagePage supplies it from the real transaction list. The singular
   * receiptUploaded/receiptDueDate/receiptStatus fields below are a
   * single-task fallback for contexts with no live transaction data (dev
   * fixture gallery, unit tests).
   */
  receiptTasks?: ReceiptTaskInput[];
  receiptDueDate?: string | null;
  receiptUploaded?: boolean;
  receiptStatus?: string | null;
}

// Just-submitted, awaiting first review → the "submitted and under review" anchor.
const SUBMITTED_REVIEW_STATES = new Set<string>([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

// Nothing left to do → the "you're all caught up" empty state.
const CAUGHT_UP_STATES = new Set<string>([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

// A rejected/changes-needed receipt blocks progress the same way "user on hold"
// does in the Figma spec — treat both as the Error/red urgency.
const BLOCKED_RECEIPT_STATUSES = new Set(['rejected', 'changes_needed']);

// A due date this close (or passed) reads as Warning/Error even before the
// receipt is rejected outright.
const RECEIPT_WARNING_WINDOW_DAYS = 3;

/**
 * Default/Warning/Error urgency for the upload-receipt task (Figma: Green on
 * swipe, Yellow nearing due date, Red once blocked/overdue).
 */
export function deriveReceiptUrgency(
  receiptStatus: string | null | undefined,
  receiptDueDate: string | null | undefined
): AttentionTaskUrgency {
  if (receiptStatus && BLOCKED_RECEIPT_STATUSES.has(receiptStatus)) {
    return 'error';
  }
  if (receiptDueDate) {
    // Parse the calendar date directly (see chipFor's note in
    // needs-your-attention.gts) to avoid a UTC-parsed off-by-one day.
    const datePart = receiptDueDate.split('T')[0] ?? '';
    const due = new Date(`${datePart}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'error';
    if (diffDays <= RECEIPT_WARNING_WINDOW_DAYS) return 'warning';
  }
  return 'default';
}

export function deriveAttention(app: AttentionInput): AttentionModel {
  // Terminal, system-closed state — none of the actionable tasks below are
  // still relevant (e.g. an unspent CourseCash card or a pending evidence
  // upload from before the request was abandoned), so short-circuit before
  // any of them get built rather than merely hiding the now-stale anchors.
  if (app.state === 'TAS.ApplicationState.ABANDONED') {
    return { visible: true, anchors: [{ kind: 'abandoned' }], tasks: [], pendingCount: 0 };
  }

  const tasks: AttentionTask[] = [];
  const anchors: AttentionAnchor[] = [];

  const reserved = app.amounts?.course_cash?.reserved ?? 0;
  const used = app.amounts?.course_cash?.used ?? 0;
  const hasCourseCash = reserved > 0;
  const courseCashUsed = hasCourseCash && used > 0;

  // Use CourseCash — present whenever a card is issued. Completes once spent; while
  // completed it stays in the rail list but is filtered out of the NYA cards.
  if (hasCourseCash) {
    tasks.push({
      id: 'use-course-cash',
      kind: 'use-course-cash',
      status: courseCashUsed ? 'completed' : 'pending',
      dueDate: app.courseCashUseByDate ?? null,
    });
  }

  // Upload CourseCash receipt — one task per swipe still needing a receipt.
  if (courseCashUsed) {
    if (app.receiptTasks) {
      for (const receiptTask of app.receiptTasks) {
        tasks.push({
          id: `upload-receipt-${receiptTask.id}`,
          kind: 'upload-receipt',
          status: 'pending',
          dueDate: receiptTask.dueDate ?? null,
          urgency: deriveReceiptUrgency(receiptTask.status, receiptTask.dueDate),
          amount: receiptTask.amount ?? null,
          transactionDate: receiptTask.transactionDate ?? null,
        });
      }
    } else if (!app.receiptUploaded) {
      tasks.push({
        id: 'upload-receipt',
        kind: 'upload-receipt',
        status: 'pending',
        dueDate: app.receiptDueDate ?? null,
        urgency: deriveReceiptUrgency(app.receiptStatus, app.receiptDueDate),
      });
    }
  }

  // Submit evidence — required whenever the template asks for completion
  // documents AND/OR a course grade (independent requirements — a template
  // can require a grade with no documents, or vice versa). Visible whenever
  // required AND either still incomplete OR the application hasn't actually
  // been submitted yet (state ATTEND). The ATTEND half matters once
  // everything required is in but the user still needs to click Submit to
  // fire the real approval transition (mirrors the legacy show page's
  // showSubmitForEvidenceReview) — the card must not disappear the instant
  // the missing-ness flips false, or the submit button vanishes with it.
  const evidenceOrGradesRequired = app.isEvidenceRequired || Boolean(app.isCourseGradeRequired);
  const evidenceOrGradesMissing =
    app.isMissingRequiredEvidence ||
    Boolean(app.isCourseGradeRequired && app.isMissingCourseGrades);
  if (
    evidenceOrGradesRequired &&
    (evidenceOrGradesMissing || app.state === 'TAS.ApplicationState.ATTEND')
  ) {
    tasks.push({
      id: 'submit-evidence',
      kind: 'submit-evidence',
      status: evidenceOrGradesMissing ? 'pending' : 'in-progress',
      dueDate: app.evidenceDueDate ?? null,
    });
  }

  // Informational anchors render above the action cards. `isApproved` stays true from
  // COURSES_APPROVED all the way through FULFILLED, so once the application has moved
  // into its own review state (SUBMITTED_REVIEW_STATES), a terminal state
  // (CAUGHT_UP_STATES), or CourseCash has already been used, that anchor takes
  // priority over re-showing the courses-approved congratulations (Figma: the
  // "CourseCash used" NYA state shows only the used anchor, not both).
  if (
    app.isApproved &&
    !SUBMITTED_REVIEW_STATES.has(app.state) &&
    !CAUGHT_UP_STATES.has(app.state) &&
    !courseCashUsed
  ) {
    anchors.push({ kind: 'approved', approvedTotal: app.approvedTotal });
  }
  // Same review/terminal-state exclusion as the approved anchor above: `used`
  // stays true forever once spent, so without this a PENDING_EVIDENCE_APPROVAL/
  // EVIDENCE_APPROVED/FULFILLED application would keep this anchor forever and
  // never reach the submitted/all-caught-up zero-state branches below.
  if (
    courseCashUsed &&
    !SUBMITTED_REVIEW_STATES.has(app.state) &&
    !CAUGHT_UP_STATES.has(app.state)
  ) {
    anchors.push({ kind: 'course-cash-used' });
  }
  // The application/evidence was just submitted and is awaiting the approver's
  // decision — this must surface regardless of any other task still pending
  // (e.g. an unspent CourseCash card), or a request can sit in review with
  // nothing on the panel acknowledging it (the submit-evidence task itself is
  // gone by design once submitted — see deriveAttention's task section above).
  if (SUBMITTED_REVIEW_STATES.has(app.state)) {
    const isEvidenceReview = app.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';
    anchors.push({ kind: isEvidenceReview ? 'evidence-submitted' : 'submitted' });
  }

  const pendingCount = tasks.filter((task) => task.status !== 'completed').length;

  // Zero actionable + zero anchor → the one remaining quiet variant.
  if (pendingCount === 0 && anchors.length === 0) {
    if (CAUGHT_UP_STATES.has(app.state)) {
      return { visible: true, anchors: [{ kind: 'all-caught-up' }], tasks, pendingCount: 0 };
    }
    // Under review with nothing to surface → suppress the panel entirely.
    return { visible: false, anchors: [], tasks, pendingCount: 0 };
  }

  return { visible: true, anchors, tasks, pendingCount };
}
